import { valibotResolver } from '@hookform/resolvers/valibot'
import { useRouter } from '@tanstack/react-router'
import { Checkbox, Form, Input } from 'antd'
import { type FC, type FormEvent, useCallback, useEffect } from 'react'
import { Controller } from 'react-hook-form'
import toast from 'react-hot-toast'
import { FaCheckCircle } from 'react-icons/fa'

import {
  BasicTable,
  BasicTbody,
  BasicTd,
  BasicTh,
  BasicTr,
  Button,
  HelpLabel,
  ModalBody,
  ModalFooter,
  RequiredTag
} from '@/components/elements'
import {
  ACTIONS,
  CONFIRMATION_MESSAGES,
  DESCRIPTIONS,
  ERROR_MESSAGES,
  EXAMPLES,
  LABELS,
  STATUSES,
  SUCCESS_MESSAGES
} from '@/constants/common/message.constants'
import { AreaSelect } from '@/features/areas/components'
import { useConfig } from '@/features/config/hooks'
import { updateCustomerSiteGroupCode } from '@/features/customers/api'
import { useCustomersSelectQuery } from '@/features/customers/hooks'
import {
  resolveSiteGroupCode,
  toastApiErrorMessage
} from '@/features/shared/utils'
import { useSiteMiddleName } from '@/features/sites/hooks'
import { useAntdModal } from '@/libs/antd/hooks'
import { type CustomerEntity, type SiteExEntity } from '@/libs/api'
import { useOnChangeForm } from '@/libs/hooks'

import { createSite, updateSite, type UpdateSiteRequest } from '../../api'
import { type CreateSiteDto, CreateSiteDtoSchema } from '../../models'

type Props = {
  tenantKey: string
  dcId?: number
  updateRequest: UpdateSiteRequest | null
  /**
   * 地点選択ポップアップに荷主選択の概念がある場合に渡す（#3669）。
   * - undefined: 荷主選択の概念が無いフォーム（フリー時間タブ等）。「荷主関連付け」欄自体を表示しない
   * - null: 荷主選択の概念はあるが未選択。「荷主関連付け」欄は表示しチェックボックスをdisabledにする
   * - CustomerEntity: 選択中の荷主。この荷主と地点をその場で関連付けられる
   */
  customer?: CustomerEntity | null
  /**
   * 新規作成時の一部項目の初期値（呼び出し元の文脈から事前入力したい場合に使う）。
   * updateRequest 指定時（編集モード）は無視される。省略時は空欄（従来どおり）。
   */
  initialValues?: Partial<Pick<CreateSiteDto, 'name' | 'code'>>
  onSuccess: (site: SiteExEntity) => Promise<void>
  onCancel: () => void
}

export const SiteModalContent: FC<Props> = ({
  tenantKey,
  dcId,
  updateRequest,
  customer,
  initialValues,
  onSuccess,
  onCancel
}) => {
  const isCreateForm = updateRequest === null
  const useSiteMiddleNameValue = useSiteMiddleName()
  const [modal, modalContext] = useAntdModal()

  // 関連付け前の地点の荷主関連付けコード（新規作成時は常にnull）
  const originalSiteGroupCode = updateRequest?.dto.siteGroupCode ?? null
  const resolvedSiteGroupCode = customer ? resolveSiteGroupCode(customer) : null
  // すでに選択中の荷主と同じコードで関連付いている場合はチェックボックスを操作不可にする
  const isAlreadyLinked =
    !!customer &&
    originalSiteGroupCode !== null &&
    originalSiteGroupCode === resolvedSiteGroupCode

  // 地点の現コードを持つ荷主を逆引きして「現在の関連荷主」表示に使う（#3669）
  const { data: allCustomers } = useCustomersSelectQuery(tenantKey, {
    disabled: customer === undefined,
    dcIds: dcId !== undefined ? [dcId] : undefined
  })
  const linkedCustomer = originalSiteGroupCode
    ? allCustomers?.find((c) => c.siteGroupCode === originalSiteGroupCode)
    : undefined

  const {
    control,
    setFocus,
    setValue,
    watch,
    handleSubmit,
    formState: { isSubmitting, isValid, errors }
  } = useOnChangeForm<CreateSiteDto>({
    defaultValues: updateRequest
      ? {
          ...updateRequest.dto,
          postalCode: updateRequest.dto.postalCode ?? '',
          prefecture: updateRequest.dto.prefecture ?? '',
          city: updateRequest.dto.city ?? '',
          blockNumber: updateRequest.dto.blockNumber ?? '',
          buildingName: updateRequest.dto.buildingName ?? '',
          contactName: updateRequest.dto.contactName ?? '',
          phoneNumber: updateRequest.dto.phoneNumber ?? '',
          displayNameMiddle: updateRequest.dto.displayNameMiddle ?? '',
          displayNameShort: updateRequest.dto.displayNameShort ?? '',
          siteGroupCode: originalSiteGroupCode
        }
      : {
          code: initialValues?.code ?? '',
          name: initialValues?.name ?? '',
          displayNameShort: '',
          displayNameMiddle: '',
          location: '',
          note: '',
          postalCode: '',
          prefecture: '',
          city: '',
          blockNumber: '',
          buildingName: '',
          contactName: '',
          phoneNumber: '',
          searchAreaIds: [],
          contractAreaId: null,
          siteGroupCode: null
        },
    resolver: valibotResolver(CreateSiteDtoSchema)
  })

  const watchedSiteGroupCode = watch('siteGroupCode')
  const isLinkChecked =
    resolvedSiteGroupCode !== null &&
    watchedSiteGroupCode === resolvedSiteGroupCode

  const handleLinkCheckboxChange = useCallback(
    (checked: boolean) => {
      if (!customer || resolvedSiteGroupCode === null) return
      if (!checked) {
        setValue('siteGroupCode', originalSiteGroupCode, {
          shouldValidate: true
        })
        return
      }
      if (
        originalSiteGroupCode !== null &&
        originalSiteGroupCode !== resolvedSiteGroupCode
      ) {
        modal.confirm({
          title: CONFIRMATION_MESSAGES.OVERWRITE_SITE_GROUP_CODE(customer.name),
          centered: true,
          onOk: () =>
            setValue('siteGroupCode', resolvedSiteGroupCode, {
              shouldValidate: true
            })
        })
        return
      }
      setValue('siteGroupCode', resolvedSiteGroupCode, { shouldValidate: true })
    },
    [customer, modal, originalSiteGroupCode, resolvedSiteGroupCode, setValue]
  )

  const router = useRouter()

  const onSubmit = useCallback(
    async (event: FormEvent) => {
      // formが入れ子になっている場合の対応
      // - submitイベントの親要素への伝播を止める
      if (event) {
        if (typeof event.preventDefault === 'function') {
          event.preventDefault()
        }
        if (typeof event.stopPropagation === 'function') {
          event.stopPropagation()
        }
      }
      return handleSubmit(async (formValue) => {
        try {
          const { data } = isCreateForm
            ? await createSite({
                tenantKey,
                dto: {
                  ...formValue,
                  dcId: dcId ?? undefined
                }
              })
            : await updateSite({
                tenantKey,
                siteId: updateRequest.siteId,
                dto: {
                  ...formValue,
                  // 有効なデータとなるように固定
                  enable: true
                }
              })

          // 荷主にコードが未設定で新たに関連付けた場合、荷主側にも同じコードを設定する（#3669）
          const customerHadNoCode =
            !!customer &&
            (customer.siteGroupCode == null || customer.siteGroupCode === '')
          if (
            customer &&
            customerHadNoCode &&
            formValue.siteGroupCode === resolveSiteGroupCode(customer)
          ) {
            try {
              await updateCustomerSiteGroupCode({
                tenantKey,
                customerId: customer.id,
                dto: { siteGroupCode: formValue.siteGroupCode }
              })
            } catch (error) {
              toastApiErrorMessage(
                error,
                ERROR_MESSAGES.FAILED_TO_LINK_SITE_WITH_CUSTOMER
              )
            }
          }

          onSuccess(data)
          toast.success(
            isCreateForm
              ? SUCCESS_MESSAGES.REGISTERED_SITE
              : SUCCESS_MESSAGES.UPDATED_SITE
          )
        } catch (error) {
          toastApiErrorMessage(
            error,
            isCreateForm
              ? ERROR_MESSAGES.FAILED_TO_REGISTER_SITE
              : ERROR_MESSAGES.FAILED_TO_UPDATE_SITE
          )
        }
      })(event)
    },
    [
      handleSubmit,
      isCreateForm,
      onSuccess,
      tenantKey,
      updateRequest,
      dcId,
      customer
    ]
  )

  useEffect(() => {
    const unsubscribe = router.subscribe('onResolved', () => {
      onCancel()
    })
    return () => {
      unsubscribe()
    }
  }, [router, onCancel])

  useEffect(() => {
    setFocus('name')
  }, [setFocus])

  const { areas } = useConfig()

  return (
    <form onSubmit={onSubmit}>
      <ModalBody>
        <BasicTable className="w-full table-fixed">
          <colgroup>
            <col className="w-[170px]" />
            <col />
            <col className="w-[150px]" />
            <col />
          </colgroup>
          <BasicTbody>
            <BasicTr>
              <BasicTh className="align-top">
                <label htmlFor="name">
                  {LABELS.SITE_DISPLAY_NAME_LONG}
                  <RequiredTag className="ml-2" />
                </label>
              </BasicTh>
              <BasicTd colSpan={3} className="align-top">
                <Controller
                  name={'name'}
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <Form.Item
                      validateStatus={error?.message ? 'error' : undefined}
                      help={error?.message}
                      className="m-0"
                    >
                      <Input
                        {...field}
                        placeholder={LABELS.SITE_DISPLAY_NAME_LONG}
                        className="w-full"
                        id={field.name}
                      />
                    </Form.Item>
                  )}
                />
              </BasicTd>
            </BasicTr>

            {/* 中表示名称（useSiteMiddleName が ON の場合のみ表示） */}
            {useSiteMiddleNameValue && (
              <BasicTr>
                <BasicTh className="align-top">
                  <label htmlFor="displayNameMiddle">
                    {LABELS.SITE_DISPLAY_NAME_MIDDLE}
                  </label>
                </BasicTh>
                <BasicTd colSpan={3} className="align-top">
                  <Controller
                    name={'displayNameMiddle'}
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <Form.Item
                        validateStatus={error?.message ? 'error' : undefined}
                        help={error?.message}
                        className="m-0"
                      >
                        <Input
                          {...field}
                          placeholder={LABELS.SITE_DISPLAY_NAME_MIDDLE}
                          className="w-full"
                          id={field.name}
                        />
                      </Form.Item>
                    )}
                  />
                </BasicTd>
              </BasicTr>
            )}

            {/* 短表示名称 */}
            <BasicTr>
              <BasicTh className="align-top">
                <label htmlFor="displayNameShort">
                  {LABELS.SITE_DISPLAY_NAME_SHORT}
                </label>
              </BasicTh>
              <BasicTd colSpan={3} className="align-top">
                <Controller
                  name={'displayNameShort'}
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <Form.Item
                      validateStatus={error?.message ? 'error' : undefined}
                      help={error?.message}
                      className="m-0"
                    >
                      <Input
                        {...field}
                        placeholder={LABELS.SITE_DISPLAY_NAME_SHORT}
                        className="w-full"
                        id={field.name}
                      />
                    </Form.Item>
                  )}
                />
              </BasicTd>
            </BasicTr>

            <BasicTr>
              <BasicTh className="align-top">
                <label htmlFor="code">
                  {LABELS.SITE_CODE}
                  <RequiredTag className="ml-2" />
                </label>
              </BasicTh>
              <BasicTd colSpan={3} className="align-top">
                <Controller
                  name={'code'}
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <Form.Item
                      validateStatus={error?.message ? 'error' : undefined}
                      help={error?.message}
                      className="m-0"
                    >
                      <Input
                        {...field}
                        placeholder={LABELS.SITE_CODE}
                        className="w-full"
                        id={field.name}
                      />
                    </Form.Item>
                  )}
                />
              </BasicTd>
            </BasicTr>
            <BasicTr>
              <BasicTh className="align-top">
                <label htmlFor="location">{LABELS.ADDRESS}</label>
              </BasicTh>
              <BasicTd colSpan={3} className="align-top">
                <Controller
                  name={'location'}
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <Form.Item
                      validateStatus={error?.message ? 'error' : undefined}
                      help={error?.message}
                      className="m-0"
                    >
                      <Input
                        {...field}
                        placeholder={LABELS.ADDRESS}
                        className="w-full"
                        id={field.name}
                      />
                    </Form.Item>
                  )}
                />
              </BasicTd>
            </BasicTr>
            <BasicTr>
              <BasicTh className="align-top">
                <label htmlFor="note">{LABELS.NOTE}</label>
              </BasicTh>
              <BasicTd colSpan={3} className="align-top">
                <Controller
                  name={'note'}
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <Form.Item
                      validateStatus={error?.message ? 'error' : undefined}
                      help={error?.message}
                      className="m-0"
                    >
                      <Input
                        {...field}
                        placeholder={LABELS.NOTE}
                        className="w-full"
                        id={field.name}
                      />
                    </Form.Item>
                  )}
                />
              </BasicTd>
            </BasicTr>

            <BasicTr>
              <BasicTh className="align-top">
                <label htmlFor="contactName">{LABELS.CONTACT_NAME}</label>
              </BasicTh>
              <BasicTd className="align-top">
                <Controller
                  name={'contactName'}
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <Form.Item
                      validateStatus={error?.message ? 'error' : undefined}
                      help={error?.message}
                      className="m-0"
                    >
                      <Input
                        {...field}
                        placeholder={LABELS.CONTACT_NAME}
                        className="w-full"
                        id={field.name}
                      />
                    </Form.Item>
                  )}
                />
              </BasicTd>
              <BasicTh className="align-top">
                <label htmlFor="phoneNumber">
                  {LABELS.CONTACT_PHONE_NUMBER}
                </label>
              </BasicTh>
              <BasicTd className="align-top">
                <Controller
                  name={'phoneNumber'}
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <Form.Item
                      validateStatus={error?.message ? 'error' : undefined}
                      help={error?.message}
                      className="m-0"
                    >
                      <Input
                        {...field}
                        placeholder={EXAMPLES.PHONE_NUMBER}
                        className="w-full"
                        id={field.name}
                      />
                    </Form.Item>
                  )}
                />
              </BasicTd>
            </BasicTr>

            <BasicTr>
              <BasicTh className="align-top">{LABELS.SEARCH_SETTINGS}</BasicTh>
              <BasicTd colSpan={3} className="align-top">
                <Controller
                  name={'searchAreaIds'}
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <Form.Item
                      validateStatus={error?.message ? 'error' : undefined}
                      help={error?.message}
                      className="m-0"
                    >
                      <AreaSelect
                        {...field}
                        showSearch
                        areas={areas}
                        mode="multiple"
                        placeholder={STATUSES.UNSELECTED}
                        id={field.name}
                        popupMatchSelectWidth={false}
                        allowClear
                        className="w-full"
                        onChange={(value) => {
                          field.onChange(value)
                        }}
                      />
                    </Form.Item>
                  )}
                />
              </BasicTd>
            </BasicTr>

            <BasicTr>
              <BasicTh className="align-top">
                {LABELS.CONTRACT_SETTINGS}
              </BasicTh>
              <BasicTd colSpan={3} className="align-top">
                <Controller
                  name={'contractAreaId'}
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <Form.Item
                      validateStatus={error?.message ? 'error' : undefined}
                      help={error?.message}
                      className="m-0"
                    >
                      <AreaSelect
                        {...field}
                        showSearch
                        areas={areas}
                        placeholder={DESCRIPTIONS.SELECT_CONTRACT_AREA}
                        id={field.name}
                        popupMatchSelectWidth={false}
                        allowClear
                        className="w-full"
                        onChange={(value) => {
                          field.onChange(value)
                        }}
                      />
                    </Form.Item>
                  )}
                />
              </BasicTd>
            </BasicTr>

            {customer !== undefined && (
              <BasicTr>
                <BasicTh className="align-top">
                  <HelpLabel
                    title={DESCRIPTIONS.CUSTOMER_SITE_GROUP_LINK_TOOLTIP}
                  >
                    {LABELS.CUSTOMER_SITE_GROUP_LINK}
                  </HelpLabel>
                </BasicTh>
                <BasicTd colSpan={3} className="align-top">
                  <div className="mb-1 text-xs text-neutral-400">
                    {linkedCustomer
                      ? DESCRIPTIONS.CURRENT_CUSTOMER_SITE_GROUP_LINK(
                          linkedCustomer.id,
                          linkedCustomer.name
                        )
                      : DESCRIPTIONS.CURRENT_CUSTOMER_SITE_GROUP_LINK_NONE}
                  </div>
                  <Checkbox
                    checked={isLinkChecked}
                    disabled={!customer || isAlreadyLinked}
                    onChange={(e) => handleLinkCheckboxChange(e.target.checked)}
                  >
                    {customer
                      ? DESCRIPTIONS.LINK_SITE_WITH_CUSTOMER(customer.name)
                      : DESCRIPTIONS.SELECT_CUSTOMER_TO_RELINK_SITE_GROUP}
                  </Checkbox>
                  {isAlreadyLinked && (
                    <div className="mt-1 text-xs text-neutral-400">
                      {DESCRIPTIONS.ALREADY_LINKED_WITH_CUSTOMER}
                    </div>
                  )}
                  {errors.siteGroupCode?.message && (
                    <div className="text-red-500 mt-1 text-xs">
                      {errors.siteGroupCode.message}
                    </div>
                  )}
                </BasicTd>
              </BasicTr>
            )}
          </BasicTbody>
        </BasicTable>
      </ModalBody>
      <ModalFooter>
        <Button onClick={onCancel}>{ACTIONS.CANCEL}</Button>
        <Button
          type="primary"
          icon={<FaCheckCircle />}
          htmlType="submit"
          disabled={!isValid || Object.keys(errors).length > 0}
          loading={isSubmitting}
        >
          {isCreateForm ? ACTIONS.REGISTER : ACTIONS.UPDATE}
        </Button>
      </ModalFooter>
      {modalContext}
    </form>
  )
}
